/**
 * Income Audit Log Page - Admin Dashboard
 * View audit history for income entries
 */
import { useState, useEffect } from 'react';
import { api, type IncomeAuditLog } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useOrganizationChange } from '../hooks/useOrganizationChange';

export function IncomeAuditPage() {
  const { token } = useAuth();
  const [auditLogs, setAuditLogs] = useState<IncomeAuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Refresh when organization changes
  useOrganizationChange(() => {
    setPage(1);
    loadData();
  });

  useEffect(() => {
    if (token) {
      api.setToken(token);
      loadData();
    }
  }, [token, page]);

  const loadData = async () => {
    setIsLoading(true);
    const result = await api.getIncomeAuditLogs({ page, limit: 50 });
    if (result.success && result.data) {
      setAuditLogs(result.data.auditLogs);
      setTotalPages(result.data.pagination.totalPages);
    }
    setIsLoading(false);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'create':
        return 'bg-green-100 text-green-800';
      case 'update':
        return 'bg-blue-100 text-blue-800';
      case 'delete':
        return 'bg-red-100 text-red-800';
      case 'restore':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatChanges = (changesStr: string | null) => {
    if (!changesStr) return null;
    try {
      const changes = JSON.parse(changesStr);
      return Object.entries(changes).map(([key, value]: [string, unknown]) => {
        const v = value as { old?: unknown; new?: unknown };
        if (key === 'created' || key === 'deleted' || key === 'restored') {
          return null;
        }
        return (
          <div key={key} className="text-xs text-gray-500">
            <span className="font-medium">{key}:</span>{' '}
            {v.old !== undefined && (
              <span className="line-through text-red-500">{String(v.old)}</span>
            )}{' '}
            {v.new !== undefined && (
              <span className="text-green-600">{String(v.new)}</span>
            )}
          </div>
        );
      }).filter(Boolean);
    } catch {
      return null;
    }
  };

  return (
    <div className="p-4 lg:p-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Income Audit Log</h1>
        <p className="text-gray-500 mt-1">View all changes made to income entries</p>
      </div>

      {/* Audit Log List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="text-gray-500">Loading...</div>
        </div>
      ) : auditLogs.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <div className="text-gray-400 text-6xl mb-4">📋</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No audit logs yet</h3>
          <p className="text-gray-500">
            Audit logs will appear here when income entries are created or modified.
          </p>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden lg:block bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Income Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Changes
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {auditLogs.map((log) => (
                  <tr key={log.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(log.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {log.username || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getActionColor(
                          log.action
                        )}`}
                      >
                        {log.action}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {log.description || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {formatChanges(log.changes)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden space-y-4">
            {auditLogs.map((log) => (
              <div key={log.id} className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex justify-between items-start mb-2">
                  <span
                    className={`px-2 py-1 text-xs font-semibold rounded-full ${getActionColor(
                      log.action
                    )}`}
                  >
                    {log.action}
                  </span>
                  <span className="text-xs text-gray-500">{formatDate(log.createdAt)}</span>
                </div>
                <div className="mb-2">
                  <span className="text-sm font-medium text-gray-900">
                    {log.username || 'Unknown'}
                  </span>
                  {log.description && (
                    <p className="text-sm text-gray-500">{log.description}</p>
                  )}
                </div>
                <div className="text-xs text-gray-500">{formatChanges(log.changes)}</div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 border rounded-lg disabled:opacity-50"
              >
                Previous
              </button>
              <span className="px-4 py-2">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 border rounded-lg disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
